import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ConfirmationService, MessageService } from 'primeng/api';
import { FormErrorHandler } from '../../../helpers/form-error-handler.helper';
import { SharedTaskService } from '../../shared-task/shared-task.service';
import { SubTask } from '../../../models/sub-task/add-subTask.model';
import { TimeZoneService } from '../../../services/timezone.service';
import { AppMainService } from 'src/app/app-main.service';
import { TaskDetailsChildTaskDto } from '../../../models/task/task-details.model';
import { TaskType } from '../../../enums/task.enum';

@Component({
  selector: 'app-add-edit-sub-task',
  templateUrl: './add-edit-sub-task.component.html',
  styleUrl: './add-edit-sub-task.component.scss'
})
export class AddEditSubTaskComponent implements OnInit, OnChanges {
  //Input & Output
  @Input() displaySubTaskPopup: boolean = false;
  @Input() taskData: any;
  @Input() editedSubtaskDetails: TaskDetailsChildTaskDto;
  @Output() cancelClicked = new EventEmitter();
  @Output() loadNewList = new EventEmitter<boolean>();
  @Input() selectedTaskTypeId: number = 0;

  dialogHeader: string = "Create Sub Task";
  // other variables
  displaySubTask: boolean = false;
  displayUsers: boolean = false;
  selectedReassignUser: any[]= [];
  priorityOptions: any[] = [];
  selectedSubTaskIndex: any;

  //Reactive form related variables
  detailsFormErrors: any = {};
  TaskType = TaskType;
  
  subTaskDetailForm: FormGroup = new FormGroup({});
  titleForm: FormGroup;
  displayTitle: boolean = false;
  formErrorHandler: FormErrorHandler = new FormErrorHandler();
  formErrorHandlerList: FormErrorHandler[] = [];
  fieldsFormErrors: any = [];

  constructor(private sanitizer: DomSanitizer,
    private confirmationService: ConfirmationService,
    private formBuilder: FormBuilder,
    private taskService: SharedTaskService,
    private timezoneService: TimeZoneService,
    private appMainService: AppMainService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.inItSubTaskForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes?.displaySubTaskPopup?.currentValue) {
        this.displaySubTask = changes?.displaySubTaskPopup?.currentValue;
        this.getTaskPriority();
    }

    if( changes?.taskData?.currentValue &&  changes?.selectedTaskTypeId?.currentValue ) {
        this.taskData = changes?.taskData?.currentValue;
        this.selectedTaskTypeId = changes?.selectedTaskTypeId?.currentValue;
    }

    if(changes?.editedSubtaskDetails?.currentValue) {
        this.editedSubtaskDetails = changes?.editedSubtaskDetails?.currentValue;
        if(this.editedSubtaskDetails){
            this.inItSubTaskForm();
        }
    }
  }

  //#region FormSetup
  inItSubTaskForm() {
    this.dialogHeader = this.editedSubtaskDetails ? `Edit Sub Task - ${this.editedSubtaskDetails.subTaskId}` : "Create Sub Task";
    this.detailsFormErrors = {};
    this.subTaskDetailForm = this.formBuilder.group({
      subTasks: this.formBuilder.array([this.createSubTask(this.editedSubtaskDetails)])
    });

    this.titleForm = this.formBuilder.group({
      title: ['']
    });

    this.setFieldsFormErrorMessage();
  }

  createSubTask(taskDetails: TaskDetailsChildTaskDto = null): FormGroup {

    let assignedUser = null;
    let endDate = null;

    if(taskDetails){
        assignedUser = taskDetails?.reAssignedTo ?
                        {id: taskDetails?.reAssignedTo, name: taskDetails?.reAssignedToName} :
                        {id: taskDetails?.taskUsers, name: taskDetails?.taskUsersName};
         endDate = taskDetails?.endDateTime ? this.timezoneService.showDateTimeWithZone(taskDetails?.endDateTime) : null;
    }else{
      endDate = this.taskData?.endDateTime ? this.timezoneService.showDateTimeWithZone(this.taskData?.endDateTime) : null;
    }

    return this.formBuilder.group({
      id: [taskDetails?.taskId||0],
      title: [taskDetails?.name||this.taskData?.name||''],
      assignedTo: [assignedUser, Validators.required],
      priority: [taskDetails?.priorityId||2, Validators.required],
      endDate: [endDate, Validators.required],
      description: [taskDetails?.description, Validators.required]
    })
  }

  setFieldsFormErrorMessage() {
    this.fieldsFormErrors = [];
    this.formErrorHandlerList = [];
    this.getSubTaskFields(this.subTaskDetailForm).map((fieldsForm: FormGroup, itemIndex: number) => {

      this.formErrorHandlerList[itemIndex] = new FormErrorHandler();

      Object.defineProperty(this.fieldsFormErrors, itemIndex, { value: {}, writable: true })

      this.formErrorHandlerList[itemIndex].handleErrors(fieldsForm, this.fieldsFormErrors[itemIndex], {
        assignedTo: {
          required: "Select User",
        },
        priority: {
          required: "Priority is required",
        },
        endDate: {
          required: "End Date is required"
        },
        description: {
          required: "Task Details are required"
        }
      });
    })
  }

  getSubTaskFields(form) {
    return form.controls?.subTasks.controls;
  }

  get subTasks() {
    return this.subTaskDetailForm.get('subTasks') as FormArray;
  }


  addSubTask() {
    this.selectedReassignUser = null;
    this.subTasks.push(this.createSubTask());
    this.setFieldsFormErrorMessage();
  }

  removeSubTask(index: number) {
    if (this.subTasks.length > 1) {
      this.subTasks.removeAt(index);
      this.setFieldsFormErrorMessage();
    }
  }

  editTitle(index: number) {
    this.selectedSubTaskIndex = index;
    this.displayTitle = true;
    const subTask = this.subTasks.at(index);
    this.titleForm.patchValue({ title: subTask.value.title });

  }

  saveTitle() {
    const newTitle = this.titleForm.value.title;
    this.subTasks.controls[this.selectedSubTaskIndex].get('title').setValue(newTitle);
    this.displayTitle = false;
  }

  //#endregion FormSetup

  //#region AssignToFeild
  reassignUser(index: number) {
    this.selectedSubTaskIndex = index;
    this.selectedReassignUser = [];
    const subTaskControl = this.subTasks.at(index); // Get the specific subtask control
    const currentAssignedTo = subTaskControl.get('assignedTo').value;
    this.selectedReassignUser = currentAssignedTo ? JSON.parse(JSON.stringify(currentAssignedTo)) : [];
    this.displayUsers = true;
  }

  saveReAssignUser($event) {
        const selectedUser = JSON.parse(JSON.stringify($event.selectedUsers));
        this.selectedReassignUser = [...selectedUser];
    if (this.selectedReassignUser && this.selectedSubTaskIndex !== undefined) {
      const subTaskControl = this.subTasks.at(this.selectedSubTaskIndex);
      subTaskControl.get('assignedTo').setValue(this.selectedReassignUser[0]);
    }
    this.displayUsers = false;
  }

  assignUserPopupCancelClicked() {
    this.displayUsers = false;
  }

  //#endregion AssignToFeild

  //#region Save&Edit
  saveSubTask() {
    if (this.subTaskDetailForm.invalid) {
      //this.formErrorHandler.showErrorOnSubmit();
      this.formErrorHandlerList.map(formErrorHandler => { formErrorHandler.showErrorOnSubmit() });
      this.scrollToError();
      return;
    }

    if(this.editedSubtaskDetails){
        const subTask = this.subTasks.value[0];
        const subTasksPayload: SubTask = {
            id: this.editedSubtaskDetails.taskId || 0,
            taskId: this.editedSubtaskDetails.taskId || 0,
            subTaskId: this.editedSubtaskDetails.subTaskId,
            name: subTask?.title || '',
            description: subTask?.description || '',
            endDateTime: this.timezoneService.saveDateTimeWithZone(subTask.endDate).toISOString(),
            statusId: 1,
            taskUsers: subTask?.assignedTo?.id || 0,
            taskCategoryId: this.editedSubtaskDetails?.taskCategoryId||this.taskData?.taskCategoryId,
            priorityId: subTask?.priority || 0,
            orderId: this.editedSubtaskDetails?.orderId||0
        }

        this.appMainService.isShowSpinner('SaveSubTask');
        this.taskService.updateSubTask(subTasksPayload).subscribe({
            next: (response) => {
                this.appMainService.isHideSpinner('SaveSubTask');
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Sub-Task Updated Successfully'
                });
                this.loadNewList.emit(true);
                this.cancelSubTask();
            },
            error: (error) => {
                this.appMainService.isHideSpinner('SaveSubTask');
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error?.error?.message ? error?.error?.message : 'Failed To Update Sub-Task'
                });
            }
        });
    }else{
        this.appMainService.isShowSpinner('SaveSubTask');
        // Construct the payload from the form values
        const subTasksPayload: SubTask[] = this.subTasks.value.map((subTask: any, index: number) => {
          return {
            id: subTask?.id || 0,
            taskId: this.taskData?.taskId || 0,
            subTaskId: '0',
            name: subTask?.title || '',
            description: subTask?.description || '',
            endDateTime: this.timezoneService.saveDateTimeWithZone(subTask.endDate),
            statusId: 1,
            taskUsers: subTask?.assignedTo?.id || 0,
            taskCategoryId: subTask?.taskCategoryId,
            priorityId: subTask?.priority || 0,
            orderId: this.selectedTaskTypeId === TaskType?.Group ?  this.taskData?.taskOrder : index,
            parentTaskTypeId: this.selectedTaskTypeId === TaskType?.Group ? TaskType?.GroupChildTask : TaskType?.Individual
          };
        });

        this.taskService.addSubTask(subTasksPayload).subscribe({
          next: (response) => {
            this.appMainService.isHideSpinner('SaveSubTask');
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Sub-Task Added Successfully'
            });
            this.loadNewList.emit(true);
            this.cancelSubTask();
          },
          error: (error) => {
            this.appMainService.isHideSpinner('SaveSubTask');
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error?.error?.message ? error?.error?.message : 'Failed To Add Sub-Task'
            });
          }
        });
    }
  }

  cancelSubTask() {
    this.displaySubTask = false;
    this.cancelClicked.emit();
  }

  //#endregion Save&Edit

  //#region OtherMethods
  scrollToError(): void {
    const firstElementWithError = document.querySelector('.ng-invalid[formControlName]');
    this.scrollTo(firstElementWithError);

    if (!firstElementWithError) {
      const secondElementWithError = document.querySelector('.p-invalid');
      this.scrollTo(secondElementWithError);
    }
  }

  // Scroll to error element
  scrollTo(el: Element): void {
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }


  getSanitizedDescription(description: string): SafeHtml {
    return description ? this.sanitizer.bypassSecurityTrustHtml(description) : '';
  }

 
  getTaskPriority() {
    this.appMainService.isShowSpinner('priority');
    this.taskService.allTaskPriorityList().subscribe({
      next: (response) => {
      this.priorityOptions = response;
      this.appMainService.isHideSpinner('priority');
      },
      error: (error) => {
        this.appMainService.isHideSpinner('priority');
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error?.Message ?? 'Something went wrong'
        });
      }
    })

  }
  //#endregion OtherMethods
}
